/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';
import { get } from 'js/lib/user';

import EnterpriseSkillsContext from 'bundles/page-config-common/contexts/enterprise-skills/EnterpriseSkillsContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { QUERY_PARAM_VALUES } from 'bundles/unified-home-common/constants/MyLearningConstants';
import { useCurrentMyLearningTab } from 'bundles/unified-home-common/contexts/MyLearningTabContext';
import { usePageSize } from 'bundles/unified-home-common/contexts/PageSizeContext';
import LearnersLatestSkillProgressQuery from 'bundles/unified-home-common/queries/LearnersLatestSkillProgress.graphql';
import QueryAdminSelectedSkills from 'bundles/unified-home-common/queries/QueryAdminSelectedSkills.graphql';
import QueryProgressForTrendingSkillsInProgram from 'bundles/unified-home-common/queries/QueryProgressForTrendingSkillsInProgram.graphql';
import type {
  LearnersLatestSkillProgressQuery as LearnersLatestSkillProgressQueryData,
  LearnersLatestSkillProgressQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/LearnersLatestSkillProgress';
import type {
  QueryAdminSelectedSkillsQuery,
  QueryAdminSelectedSkillsQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/QueryAdminSelectedSkills';
import type {
  QueryProgressForTrendingSkillsInProgramQuery as QueryProgressForTrendingSkillsInProgramQueryData,
  QueryProgressForTrendingSkillsInProgramQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/QueryProgressForTrendingSkillsInProgram';
import { getSkillProgressSortOption } from 'bundles/unified-home-common/utils/SkillDashboardUtils';

/**
 * @description This provider is used to fetch the skills data for the enterprise skills dashboard
 */
const EnterpriseSkillsProvider = ({ skillLimit, children }: React.PropsWithChildren<{ skillLimit?: number }>) => {
  const program = useProgram();
  const programId = program?.id;
  const location = useLocation();
  const { currentTab } = useCurrentMyLearningTab();
  const { pageSize, currentPageNum } = usePageSize();

  const lastRoutePath = location.pathname.slice(location.pathname.lastIndexOf('/') + 1);

  const isOnSkillsTab = currentTab === QUERY_PARAM_VALUES.SKILLS_DASHBOARD;
  const isOnLearnerHome = lastRoutePath === program?.slug && !currentTab;

  const isOnPageWithSkillsProgress = isOnLearnerHome || isOnSkillsTab;

  const {
    timeRange: timerangeInDays,
    skillLevel: skillLevelFilter,
    includeOnlyCompletedLevelSets: includeOnlySkillsWithCompletedLevelsets,
    sortField = 'UPDATED_AT',
  } = location.query;

  const {
    data: latestSkills,
    networkStatus: latestSkillsNetworkStatus,
    loading: areLatestSkillsLoading,
    error: latestSkillsProgressError,
  } = useQuery<LearnersLatestSkillProgressQueryData, LearnersLatestSkillProgressQueryVariables>(
    LearnersLatestSkillProgressQuery,
    {
      variables: {
        // skip checks that the programId is defined
        // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
        programId: programId!!,
        includeOnlySkillsWithCompletedLevelsets: includeOnlySkillsWithCompletedLevelsets === 'true',
        limit: skillLimit || pageSize,
        cursor: ((currentPageNum - 1) * pageSize).toString(),
        sortOption: getSkillProgressSortOption(sortField),
        timerangeInDays: parseInt(timerangeInDays, 10),
        skillLevelFilter,
      },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      // queryLearnersLatestSkillProgress will get the user id from request context, but only logged in learners have skills progress
      // If the sortField is RELEVANCY, than it should be searching instead
      skip: !programId || !isOnPageWithSkillsProgress || !get?.()?.id || sortField === 'RELEVANCY',
      errorPolicy: 'all',
      ssr: false,
      notifyOnNetworkStatusChange: true,
    }
  );

  // When sortField is RELEVANCY the query above is skipped (the learner is searching, not
  // listing), so `latestSkills` goes undefined. Retain the last known real value instead of
  // resetting to false, otherwise the trending-skills query starts firing again during search.
  const hasSkillProgressRef = React.useRef(false);
  if (latestSkills !== undefined) {
    hasSkillProgressRef.current = !!latestSkills.SkillsProgress?.queryLearnersLatestSkillProgress?.hasProgressInProgram;
  }
  const hasSkillProgress = hasSkillProgressRef.current;

  const latestSkillsWithProgress = latestSkills?.SkillsProgress?.queryLearnersLatestSkillProgress?.elements ?? [];
  const totalLatestSkills =
    latestSkills?.SkillsProgress?.queryLearnersLatestSkillProgress?.pagination?.totalElements ?? 0;

  // If there are no latest skills, fallback to trending skills
  const shouldUseLatestSkills = areLatestSkillsLoading || hasSkillProgress;

  const { data: adminSelectedSkillsData, loading: adminSelectedSkillsLoading } = useQuery<
    QueryAdminSelectedSkillsQuery,
    QueryAdminSelectedSkillsQueryVariables
  >(QueryAdminSelectedSkills, {
    variables: {
      // skip checks that the programId is defined
      // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
      programId: programId!!,
      limit: skillLimit || pageSize,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-and-network',
    skip: !programId || !get?.()?.id,
    errorPolicy: 'all',
    ssr: false,
  });

  const hasProgramSpecificSkills = !!adminSelectedSkillsData?.SkillsTracking?.queryByProgram?.elements?.length;
  // Trending skills back two different fallbacks: the latest-skills list (when the learner has no
  // real progress) and adminSelectedSkills (when the program has no admin-selected skills). Only
  // skip once neither fallback is needed -- skipping whenever shouldUseLatestSkills alone silently
  // drops the adminSelectedSkills fallback for learners who do have progress.
  const trendingSkillsNotNeeded = shouldUseLatestSkills && !adminSelectedSkillsLoading && hasProgramSpecificSkills;

  const {
    data: trendingSkills,
    networkStatus: trendingSkillsStatus,
    loading: trendingSkillsLoading,
    error: trendingSkillsError,
  } = useQuery<QueryProgressForTrendingSkillsInProgramQueryData, QueryProgressForTrendingSkillsInProgramQueryVariables>(
    QueryProgressForTrendingSkillsInProgram,
    {
      variables: {
        // skip checks that the programId is defined
        // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
        programId: programId!!,
        limit: skillLimit || pageSize,
        cursor: ((currentPageNum - 1) * pageSize).toString(),
      },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-and-network',
      skip: !programId || !get?.()?.id || sortField === 'RELEVANCY' || trendingSkillsNotNeeded,
      errorPolicy: 'all',
      ssr: false,
      notifyOnNetworkStatusChange: true,
    }
  );

  const networkStatus = shouldUseLatestSkills ? latestSkillsNetworkStatus : trendingSkillsStatus;
  const trendingSkillsWithProgress =
    trendingSkills?.SkillsProgress?.queryProgressForTrendingSkillsInProgram?.elements ?? [];
  const totalTrendingSkils =
    trendingSkills?.SkillsProgress?.queryProgressForTrendingSkillsInProgram?.pagination?.totalElements ?? 0;
  const adminSelectedSkills = adminSelectedSkillsData?.SkillsTracking?.queryByProgram?.elements?.map(
    ({ skill }) => skill
  );

  // If there is an error, hasSkillProgress will always be false so it incorrectly returns trending skills
  const hasError = Boolean(latestSkillsProgressError || trendingSkillsError);

  return (
    <EnterpriseSkillsContext.Provider
      value={{
        latestSkillsWithProgress: shouldUseLatestSkills ? latestSkillsWithProgress : trendingSkillsWithProgress,
        hasSkillProgress,
        // this loading state captures initial loading as well as refetching, taken from isNetworkRequestInFlight
        // (https://github.com/apollographql/apollo-client/blob/main/src/core/networkStatus.ts), which won't import correctly in snowpack or I would use it directly
        // 7 is their highest loading status
        areLatestSkillsLoading: networkStatus ? networkStatus < 7 : false,
        totalLatestSkills: shouldUseLatestSkills ? totalLatestSkills : totalTrendingSkils,
        hasProgramSpecificSkills,
        // If there are no admin selected skills, fallback to trending skills
        adminSelectedSkills: hasProgramSpecificSkills
          ? adminSelectedSkills
          : trendingSkillsWithProgress.map(({ skill }) => skill),
        areAdminSelectedSkillsLoading: adminSelectedSkillsLoading || trendingSkillsLoading,
        hasError,
      }}
    >
      {children}
    </EnterpriseSkillsContext.Provider>
  );
};

export default EnterpriseSkillsProvider;
